import { useCallback } from 'react'
import { useNavigate, useParams } from 'react-router'
import { add, format } from 'date-fns'
import { getDirectLocalStorageState } from 'e2e/fixtures/directLocalStorageState'
import styled from 'styled-components'

import StagingLogo from './assets/StagingLogo.svg?react'
import { PagePartialState } from 'directFlow/state/persistentState/type'

import config from 'env'
import { PaymentTypeIntention } from 'types'
import { useQuoteHandler } from 'src/store/useQuoteHandler'
import { useAbTests } from 'utils/abTests/abTests'

const clearFeatureFlagOverrides = () => localStorage.removeItem('FF_KEYS')

const injectLocalStorageState = (quoteId: string, quoteData?: PagePartialState) =>
  localStorage.setItem(
    `${quoteId}_DIRECT`,
    JSON.stringify(getDirectLocalStorageState({ quoteOverride: quoteData })),
  )

export const StagingStateHelper = () => {
  const { quoteId } = useParams<{ quoteId: string }>()
  const {
    preQuoteAbTests: { growth_payment_intention_question },
  } = useAbTests()
  const { createQuote } = useQuoteHandler()

  if (!quoteId) {
    throw new Error('Staging helper used on a route without a quote ID')
  }

  const navigate = useNavigate()

  const handleHelperClick = useCallback(() => {
    const overrideData =
      growth_payment_intention_question === 'CONTROL'
        ? undefined
        : {
            quote: {
              inceptionDate: format(add(new Date(), { days: 14 }), 'yyy-MM-dd'),
              paymentTypeIntention: PaymentTypeIntention.ANNUAL,
            },
          }
    clearFeatureFlagOverrides()
    injectLocalStorageState(quoteId, overrideData)

    // This is a workaround to ensure that the local storage state is injected to the Zustand store
    // while we are incrementally switching to the new state management
    // TODO: remove this workaround when the new state management is fully in place and replace with MF2 M14 changes
    const localStorageState = getDirectLocalStorageState()
    createQuote(quoteId, {
      ...localStorageState,
      pageState: {
        ...localStorageState.pageState,
        ...overrideData,
      },
    })

    navigate(0)
  }, [quoteId, growth_payment_intention_question])

  if (['staging', 'local'].includes(config.env)) {
    return <StyledStagingLogo onClick={handleHelperClick} />
  }

  return null
}

const StyledStagingLogo = styled(StagingLogo)`
  position: fixed;
  right: 8px;
  top: 35px;
  z-index: 5;
  filter: drop-shadow(1px 1px 1px rgb(0 0 0 / 0.6));
  cursor: pointer;
`
